/**
 * Часовые пояса.
 *
 * Список берётся у платформы, а не зашивается: набор зон меняется решениями
 * правительств несколько раз в год, и зашитый список устаревает молча.
 *
 * Зачем пояс вообще спрашивается. Дневная квота бесплатного тира сбрасывается
 * по МЕСТНОМУ времени пользователя (docs/DECISIONS.md, F5). У конкурента сброс
 * идёт в полночь UTC - в Душанбе это 05:00, то есть квота обнуляется посреди
 * ночного эфира. Поэтому поле видимое и редактируемое, а не спрятанное.
 */

/** Пояс, определённый браузером. Он же значение по умолчанию при регистрации. */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Текущее время в зоне - подпись в списке: по ней проще узнать свою. */
export function timeInZone(zone: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      timeZone: zone,
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date());
  } catch {
    return '';
  }
}

/**
 * Подпись пояса ПО-РУССКИ: название пояса и смещение средствами браузера.
 *
 * Решение владельца 2026-09-30 (2). До единицы 22 здесь стоял идентификатор
 * базы IANA с пробелами - «Asia / Dushanbe»: латиница в русском интерфейсе,
 * объявленная исключением словаря. Теперь - «Таджикистан, GMT+5»: название
 * даёт `Intl` (`longGeneric`), смещение - он же (`shortOffset`). Идентификатор
 * IANA остаётся ЗНАЧЕНИЕМ: его хранит база, по нему считается сброс квоты.
 *
 * Хвост «, стандартное время» снимается: он есть у одних поясов и нет у
 * других, и в списке читается как различие, которого нет.
 */
export function zoneLabel(zone: string): string {
  if (zone === 'UTC' || zone === 'Etc/UTC') return 'Всемирное время, GMT+0';

  try {
    const part = (options: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat('ru-RU', { timeZone: zone, ...options })
        .formatToParts(new Date())
        .find((item) => item.type === 'timeZoneName')?.value ?? '';
    const name = part({ timeZoneName: 'longGeneric' }).replace(/, стандартное время$/, '');
    const offset = part({ timeZoneName: 'shortOffset' });

    return name === '' || name === offset ? offset : `${name}, ${offset}`;
  } catch {
    return zone;
  }
}

/** Смещение пояса от UTC в минутах сейчас - для порядка в списке. */
function offsetMinutes(zone: string): number {
  try {
    const value =
      new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
        .formatToParts(new Date())
        .find((item) => item.type === 'timeZoneName')?.value ?? 'GMT';
    const match = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(value);

    if (match === null) return 0;

    const minutes = Number(match[2]) * 60 + Number(match[3] ?? 0);

    return match[1] === '-' ? -minutes : minutes;
  } catch {
    return 0;
  }
}

/**
 * Варианты для списка: одна строка на одну подпись.
 *
 * У 418 поясов платформы русских подписей 164: «Восточная Европа, GMT+3» -
 * это и Киев, и Рига, и Вильнюс. Сброс квоты у них в один и тот же момент,
 * и десять одинаковых строк подряд только мешают найти свою. Представителем
 * подписи становится ПОЯС ПОЛЬЗОВАТЕЛЯ, если он в ней есть: значение по
 * умолчанию обязано быть в списке. Порядок - по смещению, затем по названию.
 */
export function timezoneOptions(
  current: string = detectTimezone(),
): { value: string; label: string; offset: number }[] {
  const seen = new Map<string, { value: string; label: string; offset: number }>();

  for (const zone of listTimezones(current)) {
    const label = zoneLabel(zone);
    const had = seen.get(label);

    if (had === undefined || zone === current) {
      seen.set(label, { value: zone, label, offset: offsetMinutes(zone) });
    }
  }

  return [...seen.values()].sort(
    (a, b) => a.offset - b.offset || a.label.localeCompare(b.label, 'ru'),
  );
}

/**
 * Список зон для выбора. ВСЕГДА содержит текущую зону пользователя.
 *
 * ПОЧЕМУ ЭТО НЕ ПЕРЕСТРАХОВКА. Платформа противоречит сама себе: браузер с
 * системным временем UTC отдаёт `Intl.DateTimeFormat().resolvedOptions()
 * .timeZone === 'UTC'`, а в `Intl.supportedValuesOf('timeZone')` зоны `UTC`
 * НЕТ - там 418 зон вида `Europe/Moscow`, и ни `UTC`, ни `Etc/UTC` среди них
 * не значится. Значение по умолчанию оказывалось вне списка вариантов.
 *
 * Чем это кончалось: `<select>` не может держать значение, которому нет
 * `<option>`, компонент просил FlyonUI выставить его вручную, тот искал пункт,
 * не находил и падал с `Cannot set properties of undefined`. React снимал
 * дерево - и пользователь с системным временем UTC видел на регистрации
 * БЕЛЫЙ ЭКРАН. Не только в CI: так настроен любой сервер, любая виртуалка и
 * часть настольных систем.
 *
 * Поэтому текущая зона подмешивается в список, а не предполагается в нём.
 */
export function listTimezones(current: string = detectTimezone()): string[] {
  const supported = (Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf;

  if (typeof supported === 'function') {
    try {
      return withCurrent(supported('timeZone'), current);
    } catch {
      // падаем в запасной список ниже
    }
  }

  // Запасной список - зоны целевого рынка плюс UTC. Продукт для СНГ, Украины
  // и Грузии (CLAUDE.md), и в старом браузере важнее не остаться без выбора.
  return withCurrent(
    [
      'UTC',
      'Europe/Kaliningrad',
      'Europe/Kyiv',
      'Europe/Minsk',
      'Europe/Moscow',
      'Europe/Samara',
      'Asia/Tbilisi',
      'Asia/Yerevan',
      'Asia/Baku',
      'Asia/Almaty',
      'Asia/Aqtobe',
      'Asia/Ashgabat',
      'Asia/Bishkek',
      'Asia/Dushanbe',
      'Asia/Tashkent',
      'Asia/Yekaterinburg',
      'Asia/Omsk',
      'Asia/Novosibirsk',
      'Asia/Krasnoyarsk',
      'Asia/Irkutsk',
      'Asia/Vladivostok',
    ],
    current,
  );
}

/** Текущая зона - первой строкой, если платформа её в списке не назвала. */
function withCurrent(zones: readonly string[], current: string): string[] {
  return zones.includes(current) ? [...zones] : [current, ...zones];
}
